import { api } from "./api";

interface LoginResponse {
  accessKey: string;

  userId: number;

  user: {
    id: number;

    name: string | null;

    lastName: string | null;

    email: string;

    avatar: string | null;

    accountType: string | null;
  };
}

interface TwoFactorRequiredResponse {
  requiresTwoFactor: true;
}

type LoginResult = LoginResponse | TwoFactorRequiredResponse;

interface TwoFactorSetupResponse {
  qrCodeUrl: string;

  secret: string;
}

interface TeamMember {
  id: string;

  memberId: number;

  role: string;

  joinedAt: string;

  name: string | null;

  lastName: string | null;

  email: string | null;

  avatar: string | null;
}

interface PendingInvitation {
  id: string;

  email: string;

  status: string;

  createdAt: string;
}

interface TeamMembersResponse {
  members: TeamMember[];

  pendingInvitations: PendingInvitation[];
}

interface DeviceInfo {
  tokenId: string;

  clientType: "desktop" | "mobile" | "web" | null;

  clientApp?: "web" | "guest" | "desktop" | "mobile" | null;

  userAgent: string | null;

  ipAddress: string | null;

  lastUsedAt: string;

  createdAt: string;

  isCurrent: boolean;

  userId?: number;

  userName?: string | null;

  userEmail?: string | null;
}

interface UserProfile {
  id: number;

  name: string | null;

  lastName: string | null;

  email: string | null;

  cellphone: string | null;

  avatar: string | null;

  profilePublic: boolean;

  username: string | null;

  bio: string | null;

  socials: Record<string, string> | null;

  language: string;

  country: string | null;

  accountType: string | null;

  twoFactorEnabled: boolean;
}

class AuthService {
  async login(
    email: string,

    password: string,

    invitationToken?: string,

    totpCode?: string,
  ): Promise<LoginResult> {
    return api.post<LoginResult>("/auth/login", {
      email,

      password,

      ...(invitationToken ? { invitationToken } : {}),

      ...(totpCode ? { totpCode } : {}),
    });
  }

  async validateInvite(token: string): Promise<{
    valid: boolean;

    inviterName?: string;

    inviterEmail?: string | null;

    email?: string;

    status?: string;
  }> {
    return api.get(`/auth/validate-invite/${token}`);
  }

  async register(
    name: string,

    lastName: string,

    email: string,

    password: string,

    language: string,

    invitationToken?: string,

    captchaToken?: string,
    marketingConsent = false,
    // El backend exige `termsAccepted === true` y sin eso no crea la cuenta.
    // Default `false` (no `true`): una pantalla que se lo olvide falla con un
    // 400 explicito en vez de colarse como si la persona hubiera aceptado.
    termsAccepted = false,
  ): Promise<LoginResponse> {
    return api.post<LoginResponse>("/auth/register", {
      marketingConsent,

      termsAccepted,
      name,

      lastName,

      email,

      password,

      language,

      ...(invitationToken ? { invitationToken } : {}),

      ...(captchaToken ? { captchaToken } : {}),
    });
  }

  /**
   * Trades the Electron-local accessKey for HttpOnly session cookies in
   * the WebView. Called after a deep-link auth lands so subsequent
   * requests can ride on the cookie path (closer to browser model).
   *
   * Best-effort: if the upgrade fails (older backend, transient), the
   * Authorization header path in lib/authFetch keeps working and the
   * user is not locked out.
   */
  async upgradeSession(): Promise<void> {
    await api.authPost("/auth/session/upgrade", {});
  }

  async logout(): Promise<void> {
    await api.authPost("/auth/logout", {});
  }

  /**
   * La URL con la que el escritorio arranca el OAuth de Google.
   *
   * Pasa por el /login de la web y no directo a /auth/google: el /login setea
   * el nonce por-tab antes de redirigir, y el callback rechaza la vuelta sin
   * ese nonce (anti account-confusion).
   *
   * NO lleva la constancia de términos ni el consentimiento de marketing, y no
   * es un olvido: viajaban por esta query, o sea que los declaraba quien
   * armaba la URL. Como `autoGoogle=1` dispara el redirect SIN un solo click,
   * un link con `&termsAccepted=true` creaba cuentas con una aceptación que
   * nadie dio. Los pide /signup/completar, que es una pantalla nuestra.
   */
  getGoogleAuthUrl(): string {
    const webUrl = import.meta.env.VITE_WEB_URL || "http://localhost:3002";

    const params = new URLSearchParams({ from: "desktop", autoGoogle: "1" });

    return `${webUrl}/login?${params.toString()}`;
  }

  async verifyGoogleTwoFactor(
    pendingToken: string,

    totpCode: string,
  ): Promise<LoginResponse> {
    return api.post<LoginResponse>("/auth/2fa/verify-google-login", {
      pendingToken,

      totpCode,
    });
  }

  async getMe(): Promise<UserProfile> {
    const result = await api.authGet<{ data: UserProfile }>("/auth/me");

    return result.data;
  }

  async setupTwoFactor(): Promise<TwoFactorSetupResponse> {
    return api.authPost("/auth/2fa/setup", {});
  }

  async verifyTwoFactorSetup(code: string): Promise<{ success: boolean }> {
    return api.authPost("/auth/2fa/verify-setup", { code });
  }

  async disableTwoFactor(code: string): Promise<{ success: boolean }> {
    return api.authPost("/auth/2fa/disable", { code });
  }

  async forgotPassword(
    email: string,
  ): Promise<{ success: boolean; message: string }> {
    return api.post("/auth/forgot-password", { email });
  }

  async verifyResetToken(
    token: string,
  ): Promise<{ valid: boolean; email?: string }> {
    return api.get(`/auth/reset-password/${token}`);
  }

  async resetPassword(
    token: string,

    password: string,
  ): Promise<{ success: boolean; message: string }> {
    return api.post("/auth/reset-password", { token, password });
  }

  /**
   * The backend has no end-user `change-password` endpoint (only admins do),
   * so we trigger the email-based reset flow. Named honestly because the
   * previous `changePassword(current, new)` silently discarded both inputs and
   * shipped a reset email — leaving users believing their typed `newPassword`
   * was now active. That is a credential-handling hazard, not a feature.
   */

  async requestPasswordReset(): Promise<void> {
    const me = await this.getMe();
    if (!me.email) throw new Error("User email not found");
    await this.forgotPassword(me.email);
  }

  async updateProfile(data: {
    name?: string;

    lastName?: string;

    username?: string;

    bio?: string;

    cellphone?: string;

    language?: string;

    country?: string;
  }): Promise<UserProfile> {
    return api.authPut<UserProfile>("/auth/profile", data);
  }

  async completeOnboarding(
    accountType?: string,
    country?: string,
  ): Promise<UserProfile> {
    const result = await api.authPost<{ data: UserProfile }>(
      "/auth/onboarding",

      { accountType, country },
    );

    return result.data;
  }

  async inviteTeam(
    emails: string[],
  ): Promise<{ success: boolean; invited: number }> {
    return api.authPost("/auth/invite-team", { emails });
  }

  async getTeamMembers(): Promise<TeamMembersResponse> {
    const result = await api.authGet<{ data: TeamMembersResponse }>(
      "/auth/team-members",
    );

    return result.data;
  }

  async getDevices(): Promise<DeviceInfo[]> {
    const result = await api.authGet<{ data: DeviceInfo[] }>("/auth/devices");

    return result.data;
  }

  async revokeDevice(tokenId: string): Promise<void> {
    await api.authPost<{ success: boolean }>(
      `/auth/devices/${tokenId}/revoke`,

      {},
    );
  }

  async validateToken(): Promise<boolean> {
    try {
      await this.getMe();

      return true;
    } catch (err) {
      // Only treat 401 as expired session, not network errors (ERR_CONNECTION_REFUSED)

      const message = err instanceof Error ? err.message : "";

      if (message.includes("401") || message.includes("Unauthorized")) {
        return false;
      }

      // Network error / backend down — assume session is still valid

      return true;
    }
  }
}

export type {
  LoginResponse,
  LoginResult,
  TwoFactorRequiredResponse,
  TwoFactorSetupResponse,
  UserProfile,
  TeamMember,
  PendingInvitation,
  TeamMembersResponse,
  DeviceInfo,
};

export const authService = new AuthService();

export default authService;
